import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "../../../context/user";

// --- Types ---

export type UserLocationType = "app" | "trip" | "facility" | "satellite";
export type WorldSearchType = "travel" | "accommodation";
export type SiteType = "facilities" | "geofence" | "safety-zone";
export type TripType = "travel" | "accommodation";

export type IncidentFeed = {
    id: string;
    name: string;
};

export type MapSettings = {
    // Display
    mapType: string;
    // Incidents
    incidentFeeds: IncidentFeed[];
    showIncidentRadius: boolean;
    clusterIncidents: boolean;
    // User locations
    clientID: string | undefined;
    departmentIDs: string[];
    userTypes: UserLocationType[];
    clusterUsers: boolean;
    autoReloadUsers: boolean;
    // User tracking (ephemeral — not persisted)
    trackingUserID: string | null;
    trackingUserName: string;
    trackingDeviceID: string | null;
    trackingDays: number;
    // Trips
    tripTypes: TripType[];
    tripBufferHours: number;
    // World search
    worldSearchTypes: WorldSearchType[];
    // Sites
    siteTypes: SiteType[];
    // Facilities
    showFacilities: boolean;
    showLegacyFacilities: boolean;
    // Custom overlays (multiple selection)
    selectedOverlayIDs: string[];
    // Devices layer
    showDevices: boolean;
};

type MapSettingsContextType = {
    settings: MapSettings;
    setMapType: (v: string) => void;
    setIncidentFeeds: (feeds: IncidentFeed[]) => void;
    setShowIncidentRadius: (v: boolean) => void;
    setClusterIncidents: (v: boolean) => void;
    setClientID: (id: string | undefined) => void;
    setDepartmentIDs: (ids: string[]) => void;
    setUserTypes: (types: UserLocationType[]) => void;
    setClusterUsers: (v: boolean) => void;
    setAutoReloadUsers: (v: boolean) => void;
    setTripTypes: (types: TripType[]) => void;
    setTripBufferHours: (hours: number) => void;
    setTrackingUserID: (id: string | null, name?: string, days?: number) => void;
    setTrackingDeviceID: (id: string | null) => void;
    setTrackingDays: (days: number) => void;
    setWorldSearchTypes: (types: WorldSearchType[]) => void;
    setSiteTypes: (types: SiteType[]) => void;
    setShowFacilities: (v: boolean) => void;
    setShowLegacyFacilities: (v: boolean) => void;
    setSelectedOverlayIDs: (ids: string[]) => void;
    setShowDevices: (v: boolean) => void;
};

// --- Defaults ---

const DEFAULT_SETTINGS: Omit<MapSettings, "clientID"> = {
    departmentIDs: [],
    mapType: "roadmap",
    incidentFeeds: [],
    showIncidentRadius: false,
    clusterIncidents: true,
    userTypes: ["app", "trip", "satellite"],
    clusterUsers: true,
    autoReloadUsers: true,
    tripTypes: [],
    tripBufferHours: 1,
    trackingUserID: null,
    trackingUserName: "",
    trackingDeviceID: null,
    trackingDays: 1,
    worldSearchTypes: [],
    siteTypes: [],
    showFacilities: false,
    showLegacyFacilities: false,
    selectedOverlayIDs: [],
    showDevices: true,
};

const STORAGE_KEY = "operationalMapSettings";

type PersistedSettings = Omit<MapSettings, "clientID" | "trackingUserID" | "trackingDeviceID" | "trackingDays">;

function loadPersistedSettings(): Partial<PersistedSettings> {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

function savePersistedSettings(settings: MapSettings) {
    try {
        const { clientID: _clientID, trackingUserID: _t1, trackingUserName: _t1n, trackingDeviceID: _t2, trackingDays: _t3, ...persistable } = settings;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(persistable));
    } catch {
        // ignore
    }
}

// --- Context ---

export const MapSettingsContext = createContext<MapSettingsContextType | undefined>(undefined);

export function MapSettingsProvider({ children }: { children: ReactNode }) {
    const { isSuperAdmin } = useAuth();

    const [settings, setSettings] = useState<MapSettings>(() => {
        const persisted = loadPersistedSettings();
        return {
            ...DEFAULT_SETTINGS,
            ...persisted,
            // clientID always starts undefined — super admins can set it via setClientID
            clientID: undefined,
            trackingUserID: null,
            trackingUserName: "",
            trackingDeviceID: null,
            trackingDays: 1,
        };
    });

    // Persist on change (excludes clientID, trackingUserID, trackingDeviceID)
    useEffect(() => {
        savePersistedSettings(settings);
    }, [settings]);

    const setMapType = useCallback((v: string) => {
        setSettings((prev) => ({ ...prev, mapType: v }));
    }, []);

    const setIncidentFeeds = useCallback((feeds: IncidentFeed[]) => {
        setSettings((prev) => ({ ...prev, incidentFeeds: feeds }));
    }, []);

    const setShowIncidentRadius = useCallback((v: boolean) => {
        setSettings((prev) => ({ ...prev, showIncidentRadius: v }));
    }, []);

    const setClusterIncidents = useCallback((v: boolean) => {
        setSettings((prev) => ({ ...prev, clusterIncidents: v }));
    }, []);

    const setClientID = useCallback((id: string | undefined) => {
        if (!isSuperAdmin) return;
        setSettings((prev) => ({ ...prev, clientID: id }));
    }, [isSuperAdmin]);

    const setDepartmentIDs = useCallback((ids: string[]) => {
        setSettings((prev) => ({ ...prev, departmentIDs: ids }));
    }, []);

    const setUserTypes = useCallback((types: UserLocationType[]) => {
        setSettings((prev) => ({ ...prev, userTypes: types }));
    }, []);

    const setClusterUsers = useCallback((v: boolean) => {
        setSettings((prev) => ({ ...prev, clusterUsers: v }));
    }, []);

    const setAutoReloadUsers = useCallback((v: boolean) => {
        setSettings((prev) => ({ ...prev, autoReloadUsers: v }));
    }, []);

    const setTripTypes = useCallback((types: TripType[]) => {
        setSettings((prev) => ({ ...prev, tripTypes: types }));
    }, []);

    const setTripBufferHours = useCallback((hours: number) => {
        setSettings((prev) => ({ ...prev, tripBufferHours: hours }));
    }, []);

    const setTrackingUserID = useCallback((id: string | null, name = "", days?: number) => {
        setSettings((prev) => ({ ...prev, trackingUserID: id, trackingUserName: name, ...(days !== undefined ? { trackingDays: days } : {}) }));
    }, []);

    const setTrackingDeviceID = useCallback((id: string | null) => {
        setSettings((prev) => ({ ...prev, trackingDeviceID: id }));
    }, []);

    const setTrackingDays = useCallback((days: number) => {
        setSettings((prev) => ({ ...prev, trackingDays: days }));
    }, []);

    const setWorldSearchTypes = useCallback((types: WorldSearchType[]) => {
        setSettings((prev) => ({ ...prev, worldSearchTypes: types }));
    }, []);

    const setSiteTypes = useCallback((types: SiteType[]) => {
        setSettings((prev) => ({ ...prev, siteTypes: types }));
    }, []);

    const setShowFacilities = useCallback((v: boolean) => {
        setSettings((prev) => ({ ...prev, showFacilities: v }));
    }, []);

    const setShowLegacyFacilities = useCallback((v: boolean) => {
        setSettings((prev) => ({ ...prev, showLegacyFacilities: v }));
    }, []);

    const setSelectedOverlayIDs = useCallback((ids: string[]) => {
        setSettings((prev) => ({ ...prev, selectedOverlayIDs: ids }));
    }, []);

    const setShowDevices = useCallback((v: boolean) => {
        setSettings((prev) => ({ ...prev, showDevices: v }));
    }, []);

    return (
        <MapSettingsContext.Provider value={{
            settings,
            setMapType,
            setIncidentFeeds,
            setShowIncidentRadius,
            setClusterIncidents,
            setClientID,
            setDepartmentIDs,
            setUserTypes,
            setClusterUsers,
            setAutoReloadUsers,
            setTripTypes,
            setTripBufferHours,
            setTrackingUserID,
            setTrackingDeviceID,
            setTrackingDays,
            setWorldSearchTypes,
            setSiteTypes,
            setShowFacilities,
            setShowLegacyFacilities,
            setSelectedOverlayIDs,
            setShowDevices,
        }}>
            {children}
        </MapSettingsContext.Provider>
    );
}
